<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { formatDateTime } from '@/core';
import { DtBtn, DtNotice } from '@/design-system';
import { useOfflineQueue } from '@/composables/offlineQueue';

const { entries, error, legacyCount, retry, discard } = useOfflineQueue();
const router = useRouter();
const discardId = ref<string | null>(null);
const actionError = ref<string | null>(null);
async function confirmDiscard(): Promise<void> {
  if (discardId.value === null) return;
  try { await discard(discardId.value); discardId.value = null; }
  catch (cause) { actionError.value = cause instanceof Error ? cause.message : 'Não foi possível descartar o registro.'; }
}
onMounted(() => { void retry(); });
</script>

<template>
  <DtNotice
    v-if="entries.length || legacyCount || error"
    kind="warn"
    title="Envios guardados no dispositivo"
  >
    <p
      v-if="error"
      role="alert"
    >
      {{ error }}
    </p>
    <p v-if="legacyCount">
      Há {{ legacyCount }} registro(s) antigo(s) sem chave recuperável. Eles não foram enviados. Refaça a captura no atendimento; os registros antigos foram preservados.
    </p>
    <ul v-if="entries.length">
      <li
        v-for="entry in entries"
        :key="entry.id"
      >
        {{ entry.label }} · {{ formatDateTime(new Date(entry.createdAt)) }}: {{ entry.message }}
        <DtBtn
          v-if="entry.requestId"
          variant="quiet"
          @click="router.push({ name: 'chamado-detalhe', params: { id: entry.requestId } })"
        >
          Abrir chamado {{ entry.requestId.slice(0, 8) }}
        </DtBtn>
        <DtBtn
          v-if="entry.state !== 'sending'"
          variant="quiet"
          @click="discardId = entry.id"
        >
          Descartar este registro
        </DtBtn>
      </li>
    </ul>
    <DtBtn
      v-if="entries.length"
      @click="retry"
    >
      Tentar enviar novamente
    </DtBtn>
    <div
      v-if="discardId"
      role="alert"
    >
      <p>Descartar apaga a única cópia guardada neste dispositivo. Confirme somente após refazer a captura ou verificar o atendimento.</p>
      <DtBtn @click="confirmDiscard">
        Confirmar descarte
      </DtBtn>
      <DtBtn
        variant="quiet"
        @click="discardId = null"
      >
        Manter registro
      </DtBtn>
    </div>
    <p
      v-if="actionError"
      role="alert"
    >
      {{ actionError }}
    </p>
  </DtNotice>
</template>
